<script setup>
import { computed } from 'vue';
import { useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useMapGetter } from 'dashboard/composables/store';
import { frontendURL } from 'dashboard/helper/URLHelper';
import Avatar from 'next/avatar/Avatar.vue';
import ChannelIcon from 'next/icon/ChannelIcon.vue';
import Icon from 'next/icon/Icon.vue';

const props = defineProps({
  conversation: { type: Object, required: true },
  now: { type: Number, required: true },
  labelColors: { type: Object, default: () => ({}) },
});

const { t } = useI18n();
const route = useRoute();
const getInboxById = useMapGetter('inboxes/getInboxById');

const WAIT_WARNING_MINUTES = 5;
const WAIT_CRITICAL_MINUTES = 15;

const inbox = computed(() => getInboxById.value(props.conversation.inbox_id));

const href = computed(() =>
  frontendURL(
    `accounts/${route.params.accountId}/conversations/${props.conversation.id}`
  )
);

const compactDuration = seconds => {
  const minutes = Math.floor(seconds / 60);
  if (minutes < 1) return t('LIVE_VIEW.DURATION.NOW');
  if (minutes < 60) return t('LIVE_VIEW.DURATION.MINUTES', { n: minutes });
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return t('LIVE_VIEW.DURATION.HOURS', { n: hours });
  return t('LIVE_VIEW.DURATION.DAYS', { n: Math.floor(hours / 24) });
};

// waiting_since is set while the customer is waiting for a reply
const waitingSeconds = computed(() =>
  props.conversation.waiting_since
    ? Math.max(0, props.now - props.conversation.waiting_since)
    : null
);

const waitLevelClass = computed(() => {
  const minutes = (waitingSeconds.value ?? 0) / 60;
  if (minutes >= WAIT_CRITICAL_MINUTES) {
    return 'bg-n-ruby-3 text-n-ruby-11';
  }
  if (minutes >= WAIT_WARNING_MINUTES) {
    return 'bg-n-amber-3 text-n-amber-11';
  }
  return 'bg-n-alpha-2 text-n-slate-11';
});

const accentClass = computed(() => {
  const minutes = (waitingSeconds.value ?? 0) / 60;
  if (minutes >= WAIT_CRITICAL_MINUTES) return 'border-l-n-ruby-9';
  if (minutes >= WAIT_WARNING_MINUTES) return 'border-l-n-amber-9';
  return 'border-l-transparent';
});

const lastMessagePrefix = computed(() =>
  props.conversation.last_message_type === 'incoming'
    ? ''
    : `${t('LIVE_VIEW.YOU_PREFIX')} `
);
</script>

<template>
  <a
    :href="href"
    target="_blank"
    rel="noopener noreferrer"
    class="flex flex-col gap-1.5 p-3 border-l-4 rounded-lg outline outline-1 -outline-offset-1 outline-n-weak bg-n-solid-2 hover:bg-n-alpha-1 transition-colors"
    :class="accentClass"
  >
    <div class="flex items-center gap-2 min-w-0">
      <Avatar
        :name="conversation.contact_name || ''"
        :src="conversation.contact_thumbnail"
        :size="28"
        rounded-full
      />
      <span class="flex-1 min-w-0 text-sm font-medium truncate text-n-slate-12">
        {{ conversation.contact_name }}
      </span>
      <span class="text-xs tabular-nums text-n-slate-10">
        {{ `#${conversation.id}` }}
      </span>
    </div>

    <p class="text-sm line-clamp-2 text-n-slate-11">
      <span v-if="conversation.last_message_content">
        <span class="text-n-slate-10">{{ lastMessagePrefix }}</span>
        {{ conversation.last_message_content }}
      </span>
      <span v-else class="italic text-n-slate-10">
        {{ t('LIVE_VIEW.NO_MESSAGES') }}
      </span>
    </p>

    <div class="flex flex-wrap items-center gap-1.5">
      <ChannelIcon v-if="inbox" :inbox="inbox" class="size-4 text-n-slate-11" />
      <span
        v-if="waitingSeconds !== null"
        class="inline-flex items-center gap-1 px-1.5 py-0.5 text-xs font-medium rounded tabular-nums"
        :class="waitLevelClass"
      >
        <Icon icon="i-lucide-hourglass" class="size-3" />
        {{ t('LIVE_VIEW.WAITING', { time: compactDuration(waitingSeconds) }) }}
      </span>
      <span
        v-if="conversation.status === 'pending'"
        class="inline-flex items-center gap-1 px-1.5 py-0.5 text-xs font-medium rounded bg-n-blue-3 text-n-blue-11"
      >
        <Icon icon="i-lucide-bot" class="size-3" />
        {{ t('LIVE_VIEW.BOT') }}
      </span>
      <span
        v-for="label in conversation.labels"
        :key="label"
        class="px-1.5 py-0.5 text-xs rounded text-n-slate-12 bg-n-alpha-2"
      >
        <span
          class="inline-block mr-1 rounded-full size-1.5"
          :style="{ backgroundColor: labelColors[label] }"
        />{{ label }}
      </span>
    </div>
  </a>
</template>
